import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

export const RECENT_ACTIVITY_MAX_RANGE_MS = 24 * 60 * 60 * 1000;

export type RecentActivityRange = {
  startDate: Date;
  endDate: Date;
};

export type RecentActivityRangeValidation = {
  isValid: boolean;
  message?: string;
};

export type RecentActivityCsvRow = {
  event: string;
  host: string;
  path: string;
  eventId: string;
  time: string;
  amount: string;
};

export const getRecentActivityDayRange = (
  date: Date = new Date()
): RecentActivityRange => {
  const startDate = dayjs(date).startOf("day");

  return {
    startDate: startDate.toDate(),
    endDate: startDate.endOf("day").toDate(),
  };
};

export const getDefaultRecentActivityRange = (
  now: Date = new Date()
): RecentActivityRange => getRecentActivityDayRange(now);

const RECENT_ACTIVITY_DATE_PARAM_FORMAT = "YYYY-MM-DD";
const RECENT_ACTIVITY_API_DATE_PARAM_FORMAT = "YYYY-MM-DD HH:mm:ss";

export const formatRecentActivityDateParam = (date: Date) =>
  dayjs(date).format(RECENT_ACTIVITY_DATE_PARAM_FORMAT);

export const parseRecentActivityDateParam = (
  value: string | null | undefined
) => {
  if (!value) return null;

  const dateOnlyParam = dayjs(value, RECENT_ACTIVITY_DATE_PARAM_FORMAT, true);

  if (dateOnlyParam.isValid()) {
    return dateOnlyParam.startOf("day").toDate();
  }

  if (value.length === RECENT_ACTIVITY_DATE_PARAM_FORMAT.length) {
    return null;
  }

  const dateTimeParam = dayjs(
    value,
    RECENT_ACTIVITY_API_DATE_PARAM_FORMAT,
    true
  );

  if (dateTimeParam.isValid()) {
    return dateTimeParam.toDate();
  }

  const parsedDate = dayjs(value);

  return parsedDate.isValid() ? parsedDate.toDate() : null;
};

export const getRecentActivityRangeFromParams = ({
  startParam,
  endParam,
  fallbackRange,
}: {
  startParam: string | null | undefined;
  endParam: string | null | undefined;
  fallbackRange: RecentActivityRange;
}): RecentActivityRange => {
  const parsedStartDate = parseRecentActivityDateParam(startParam);
  const parsedEndDate = parseRecentActivityDateParam(endParam);

  if (parsedStartDate) {
    const startDayRange = getRecentActivityDayRange(parsedStartDate);

    return {
      startDate: startDayRange.startDate,
      endDate: parsedEndDate ?? startDayRange.endDate,
    };
  }

  return parsedEndDate
    ? getRecentActivityDayRange(parsedEndDate)
    : fallbackRange;
};

export const validateRecentActivityRange = (
  range: Partial<RecentActivityRange>,
  maxDate: Date = new Date()
): RecentActivityRangeValidation => {
  if (!range.startDate || !range.endDate) {
    return {
      isValid: false,
      message: "Select a date.",
    };
  }

  const startDate = dayjs(range.startDate);
  const endDate = dayjs(range.endDate);

  if (!startDate.isValid() || !endDate.isValid()) {
    return {
      isValid: false,
      message: "Enter a valid date.",
    };
  }

  const maxSelectableDate = dayjs(maxDate);
  if (startDate.isAfter(maxSelectableDate, "day")) {
    return {
      isValid: false,
      message: "Select today or an earlier date.",
    };
  }

  if (startDate.isAfter(endDate)) {
    return {
      isValid: false,
      message: "Start date must be before end date.",
    };
  }

  if (endDate.diff(startDate) > RECENT_ACTIVITY_MAX_RANGE_MS) {
    return {
      isValid: false,
      message: "Select a range of 24 hours or less.",
    };
  }

  return { isValid: true };
};

export const formatRecentActivityApiDateParam = (date: Date) =>
  dayjs(date).format(RECENT_ACTIVITY_API_DATE_PARAM_FORMAT);

export const formatRecentActivityRangeLabel = (range: RecentActivityRange) => {
  return formatRecentActivityDateParam(range.startDate);
};

export const formatRecentActivityExportFileName = (
  range: RecentActivityRange
) =>
  `tracify_export_recent_activity_${formatRecentActivityDateParam(
    range.startDate
  )}.csv`;

export const formatRecentActivityUtcTimestamp = (date: Date) => {
  const parsedDate = dayjs(date);

  if (!parsedDate.isValid()) return "";

  return parsedDate.utc().format("YYYY-MM-DD HH:mm:ss [UTC]");
};

const escapeCsvCell = (value: string | number | null | undefined) => {
  const stringValue =
    value === null || value === undefined ? "" : String(value);

  return `"${stringValue.replace(/"/g, '""')}"`;
};

export const createRecentActivityCsv = (rows: RecentActivityCsvRow[]) => {
  const headers = ["Event", "Host", "Path", "Event ID", "Time (UTC)", "Amount"];
  const csvRows = rows.map((row) =>
    [row.event, row.host, row.path, row.eventId, row.time, row.amount]
      .map(escapeCsvCell)
      .join(",")
  );

  return [headers.map(escapeCsvCell).join(","), ...csvRows].join("\n");
};

export const parseEventTypesParam = (
  value: string | null | undefined
): string[] =>
  (value ?? "")
    .split(",")
    .map((eventType) => eventType.trim())
    .filter((eventType) => eventType.length > 0);

export const formatEventTypesParam = (eventTypes: string[]) => {
  const cleanedEventTypes = Array.from(
    new Set(
      eventTypes
        .map((eventType) => eventType.trim())
        .filter((eventType) => eventType.length > 0)
    )
  );

  return cleanedEventTypes.length ? cleanedEventTypes.join(",") : null;
};
